import { request, requestNoContent } from './client';
import type { ProjectMemberRoleValue, ProjectTaskProgressMethodValue } from './projects';

/**
 * Tầng gọi API của mẫu dự án.
 *
 * Tách khỏi `projects.ts` thay vì thêm vào cuối tệp đó: tệp kia đã dài gần một nghìn sáu trăm dòng
 * và mẫu dự án là một nhóm điểm cuối riêng, có đường dẫn gốc riêng. Gộp thêm vào sẽ làm cả hai khó
 * tìm.
 */

/** Đường dẫn gốc của mẫu dự án. Khớp với `ProjectTemplateEndpoints.RoutePrefix`. */
const templateRoute = '/api/v1/project-templates';

/** Đường dẫn gốc của dự án — dùng cho thao tác áp mẫu. */
const projectRoute = '/api/v1/projects';

/**
 * Tên các biến dùng được trong tên công việc của mẫu.
 *
 * Khai ở đây để biểu mẫu hiển thị đúng danh sách cho người khai mẫu. Chuỗi phải khớp với
 * `ProjectTemplateVariables` ở tầng lõi — lệch một chữ thì người dùng gõ một biến không bao giờ
 * được thay, và lúc áp mẫu nó hiện nguyên dạng `{ten_khach_hang}` trong tên việc thật.
 */
export const projectTemplateVariables = {
  /** Mã dự án. */
  projectCode: 'ma_du_an',

  /** Tên dự án. */
  projectName: 'ten_du_an',

  /** Tên viết ngắn của khách hàng. */
  customerName: 'ten_khach_hang',
} as const;

/** Một mục kiểm mặc định trong mẫu. */
export interface ProjectTemplateChecklistItem {
  readonly description: string;
  readonly isRequired: boolean;
  readonly weight: number;
}

/** Một mẫu dự án. */
export interface ProjectTemplate {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly projectTypeId: string;
  readonly projectTypeName: string | null;
  readonly description: string | null;
  readonly estimatedDurationMonths: number | null;
  readonly isDefault: boolean;
  readonly versionNumber: number;
  readonly isActive: boolean;
  readonly taskCount: number;
}

/** Một công việc trong mẫu. */
export interface ProjectTemplateTask {
  readonly id: string;
  readonly parentId: string | null;

  /**
   * Số thứ tự.
   *
   * <b>Quan hệ phụ thuộc trong mẫu trỏ theo số này, không trỏ theo định danh.</b> Nhờ vậy một mẫu
   * chép sang mẫu khác vẫn giữ được quan hệ phụ thuộc.
   */
  readonly sequenceNumber: number;

  readonly name: string;
  readonly defaultDurationDays: number | null;
  readonly defaultEffortHours: number | null;
  readonly defaultAssigneeRole: ProjectMemberRoleValue | null;
  readonly defaultProgressMethod: ProjectTaskProgressMethodValue | null;
  readonly isMilestone: boolean;
  readonly isOptional: boolean;
  readonly guidanceNote: string | null;
  readonly dependsOnSequenceNumbers: readonly number[];
  readonly defaultChecklistItems: readonly ProjectTemplateChecklistItem[];
}

/** Một mẫu kèm toàn bộ cây công việc của nó. */
export interface ProjectTemplateDetail {
  readonly template: ProjectTemplate;
  readonly tasks: readonly ProjectTemplateTask[];
}

/** Điều kiện lọc danh sách mẫu. */
export interface ProjectTemplateFilter {
  readonly projectTypeId?: string | undefined;
  readonly onlyActive?: boolean | undefined;
}

/** Nội dung lập hoặc sửa một mẫu. */
export interface SaveProjectTemplatePayload {
  readonly code: string;
  readonly name: string;
  readonly projectTypeId: string;
  readonly description: string | null;
  readonly estimatedDurationMonths: number | null;
}

/** Nội dung thêm hoặc sửa một công việc trong mẫu. */
export interface SaveProjectTemplateTaskPayload {
  readonly parentId: string | null;

  /** Số thứ tự. Bằng 0 nghĩa là để máy chủ đặt xuống cuối. */
  readonly sequenceNumber: number;

  readonly name: string;
  readonly defaultDurationDays: number | null;
  readonly defaultEffortHours: number | null;
  readonly defaultAssigneeRole: ProjectMemberRoleValue | null;
  readonly defaultProgressMethod: ProjectTaskProgressMethodValue | null;
  readonly isMilestone: boolean;
  readonly isOptional: boolean;
  readonly guidanceNote: string | null;
  readonly dependsOnSequenceNumbers: readonly number[];
  readonly defaultChecklistItems: readonly ProjectTemplateChecklistItem[];
}

/** Nội dung áp một mẫu vào dự án. */
export interface ApplyProjectTemplatePayload {
  readonly projectTemplateId: string;

  /** Số thứ tự của những việc <b>tùy chọn</b> bị bỏ chọn. */
  readonly excludedSequenceNumbers: readonly number[];
}

/** Kết quả của một lần áp mẫu. */
export interface ApplyProjectTemplateResult {
  readonly createdTaskCount: number;
  readonly createdChecklistItemCount: number;
  readonly createdDependencyCount: number;
  readonly skippedOptionalCount: number;
}

/**
 * Lấy danh sách mẫu.
 *
 * Không phân trang: số mẫu của một công ty là số quy trình chuẩn của công ty đó, nên nó ở mức vài
 * chục chứ không vài nghìn. Phân trang ở đây chỉ thêm bước bấm mà không giải quyết vấn đề nào.
 */
export async function listProjectTemplates(
  filter: ProjectTemplateFilter = {},
): Promise<readonly ProjectTemplate[]> {
  const parameters = new URLSearchParams();

  if (filter.projectTypeId !== undefined) {
    parameters.set('projectTypeId', filter.projectTypeId);
  }

  if (filter.onlyActive === true) {
    parameters.set('onlyActive', 'true');
  }

  const query = parameters.size === 0 ? '' : `?${parameters.toString()}`;

  return await request<readonly ProjectTemplate[]>(`${templateRoute}${query}`);
}

/** Lấy một mẫu kèm cây công việc. */
export async function getProjectTemplate(templateId: string): Promise<ProjectTemplateDetail> {
  return await request<ProjectTemplateDetail>(`${templateRoute}/${templateId}`);
}

/** Lập một mẫu mới. */
export async function createProjectTemplate(
  payload: SaveProjectTemplatePayload,
): Promise<ProjectTemplate> {
  return await request<ProjectTemplate>(templateRoute, { method: 'POST', body: payload });
}

/** Sửa phần khai báo của một mẫu. */
export async function updateProjectTemplate(
  templateId: string,
  payload: SaveProjectTemplatePayload,
): Promise<ProjectTemplate> {
  return await request<ProjectTemplate>(`${templateRoute}/${templateId}`, {
    method: 'PUT',
    body: payload,
  });
}

/** Đặt hoặc bỏ mẫu mặc định của một loại dự án. */
export async function setProjectTemplateDefault(
  templateId: string,
  value: boolean,
): Promise<void> {
  await requestNoContent(`${templateRoute}/${templateId}/default`, {
    method: 'PUT',
    body: { value },
  });
}

/** Cho dùng lại hoặc ngừng dùng một mẫu. */
export async function setProjectTemplateActive(
  templateId: string,
  value: boolean,
): Promise<void> {
  await requestNoContent(`${templateRoute}/${templateId}/active`, {
    method: 'PUT',
    body: { value },
  });
}

/**
 * Tăng số phiên bản của mẫu.
 *
 * Thao tác riêng chứ không tự tăng mỗi lần sửa: một lần sửa lỗi chính tả trong tên việc không phải
 * một phiên bản mới của quy trình. Người khai mẫu quyết định khi nào quy trình đã đổi thật.
 */
export async function incrementProjectTemplateVersion(templateId: string): Promise<void> {
  await requestNoContent(`${templateRoute}/${templateId}/version`, { method: 'POST' });
}

/** Thêm một công việc vào mẫu. */
export async function addProjectTemplateTask(
  templateId: string,
  payload: SaveProjectTemplateTaskPayload,
): Promise<ProjectTemplateTask> {
  return await request<ProjectTemplateTask>(`${templateRoute}/${templateId}/tasks`, {
    method: 'POST',
    body: payload,
  });
}

/** Sửa một công việc trong mẫu. */
export async function updateProjectTemplateTask(
  templateId: string,
  taskId: string,
  payload: SaveProjectTemplateTaskPayload,
): Promise<ProjectTemplateTask> {
  return await request<ProjectTemplateTask>(`${templateRoute}/${templateId}/tasks/${taskId}`, {
    method: 'PUT',
    body: payload,
  });
}

/** Xóa một công việc khỏi mẫu. */
export async function removeProjectTemplateTask(
  templateId: string,
  taskId: string,
): Promise<void> {
  await requestNoContent(`${templateRoute}/${templateId}/tasks/${taskId}`, { method: 'DELETE' });
}

/**
 * Áp một mẫu vào một dự án.
 *
 * Chỉ áp được vào dự án <b>chưa có công việc nào</b>. Máy chủ chặn, và giao diện cũng chặn từ trước
 * bằng cách chỉ hiện nút khi cây công việc còn rỗng — hai lớp cho cùng một quy tắc, vì áp lần hai sẽ
 * nhân đôi cả cây và không có cách nào gỡ ra cho đúng.
 */
export async function applyProjectTemplate(
  projectId: string,
  payload: ApplyProjectTemplatePayload,
): Promise<ApplyProjectTemplateResult> {
  return await request<ApplyProjectTemplateResult>(`${projectRoute}/${projectId}/apply-template`, {
    method: 'POST',
    body: payload,
  });
}
